<template>
  <div class="kpt-detail-content-page">
    <vue-headful :title="`${remote.name} - Subscriptions`" />
    <div class="kpt-detail-content-actions" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
      <div class="buttons">
        <b-button tag="router-link"  
          :to="{ name: 'subscriptionCreate', params: { remoteId: this.remote.id, remoteName: this.remote.name, accountId: this.remote.account.id } }" 
          type="is-primary is-outlined" 
          size="is-small" 
          icon-left="plus">
          Add Subscription
        </b-button>
      </div>
    </div>
    <header style="margin-bottom: 8px">
      <h6 class="is-uppercase"><strong>Current Subscription</strong></h6>
    </header>
    <div v-if="activeSubscription" class="kpt-subscription-record is-flex">
      <div>
        <detail-field>
          <template v-slot:label>Product</template>
          <template v-slot:value>{{activeSubscription.product}}</template>
        </detail-field>

        <detail-field v-if="activeSubscription.hasSatellite">
          <template v-slot:label>Region</template>
          <template v-slot:value>{{activeSubscription.region || '-'}}</template>
        </detail-field>
        <detail-field v-if="activeSubscription.hasSatellite">
          <template v-slot:label>Data</template>
          <template v-slot:value>{{activeSubscription.dataPackage || '-'}}</template>
        </detail-field>
        <detail-field>
          <template v-slot:label>Start Date</template>
          <template v-slot:value>{{ format(addMinutes(new Date(activeSubscription.startDate), new Date(activeSubscription.startDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
        </detail-field>
        <detail-field>
          <template v-slot:label>End Date</template>
          <template v-slot:value>{{ format(addMinutes(new Date(activeSubscription.endDate), new Date(activeSubscription.endDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
        </detail-field>
        <detail-field>
          <template v-slot:label>Auto Renew</template>
          <template v-slot:value>{{ activeSubscription.autoRenew ? 'Yes' : 'No' }}</template>
        </detail-field>
        <div v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
          <detail-field>
            <template v-slot:label>Purchase Order</template>
            <template v-slot:value>{{ activeSubscription.purchaseOrder || '-' }}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Billed Account</template>
            <template v-slot:value>{{ getAccountName(activeSubscription.billedAccountId) || '-' }}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Is Billable</template>
            <template v-slot:value>{{ activeSubscription.isBillable ? `Yes` : `No` }}</template>
          </detail-field>
        </div>
      </div>
      <div class="is-flex" style="justify-content: flex-end; flex: 1 0 auto;">
          <b-button tag="router-link" type="is-link is-outlined" size="is-small" :to="{ name: 'subscriptionOverview', params: { id: activeSubscription.id }}" v-if="!isSharedRemote">View Subscription</b-button>
        </div>
    </div>
    <div v-else>
      <span>No active subscriptions.</span>
    </div>
    <hr v-if="futureSubscriptions.length">
    <header style="margin-bottom: 8px" v-if="futureSubscriptions.length">
      <h6 class="is-uppercase"><strong>Future Subscriptions</strong></h6>
    </header>
    <template v-if="futureSubscriptions.length > 0">
      <div v-for="(nonActiveSubscription, idx) in futureSubscriptions" :key="idx" class="kpt-subscription-record is-flex">
        <div>
          <detail-field>
            <template v-slot:label>Product</template>
            <template v-slot:value>{{nonActiveSubscription.productName}}</template>
          </detail-field>
          
          <detail-field v-if="nonActiveSubscription.hasSatellite">
            <template v-slot:label>Region</template>
            <template v-slot:value>{{nonActiveSubscription.regionName || '-'}}</template>
          </detail-field>
          <detail-field v-if="nonActiveSubscription.hasSatellite">
            <template v-slot:label>Data</template>
            <template v-slot:value>{{nonActiveSubscription.dataPackageName || '-'}}</template>
          </detail-field>        
          <detail-field>
            <template v-slot:label>Start Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(nonActiveSubscription.startDate), new Date(nonActiveSubscription.startDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>End Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(nonActiveSubscription.endDate), new Date(nonActiveSubscription.endDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Auto Renew</template>
            <template v-slot:value>{{ nonActiveSubscription.autoRenew ? 'Yes' : 'No' }}</template>
          </detail-field>
          <div v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
            <detail-field>
              <template v-slot:label>Purchase Order</template>
              <template v-slot:value>{{ nonActiveSubscription.purchaseOrder || '-' }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Billed Account</template>
              <template v-slot:value>{{ getAccountName(nonActiveSubscription.billedAccountId) || '-' }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Is Billable</template>
              <template v-slot:value>{{ nonActiveSubscription.isBillable ? `Yes` : `No` }}</template>
            </detail-field>
          </div>
        </div>
        <div class="is-flex" style="justify-content: flex-end; flex: 1 0 auto;">
          <b-button tag="router-link" type="is-link is-outlined" size="is-small" :to="{ name: 'subscriptionOverview', params: { id: nonActiveSubscription.id }}" v-if="!isSharedRemote">View Subscription</b-button>
        </div>
      </div>
    </template>
    <hr>
    <header style="margin-bottom: 8px">
      <h6 class="is-uppercase"><strong>Past Subscriptions</strong></h6>
    </header>
    <template v-if="pastSubscriptions.length > 0">
      <div v-for="(nonActiveSubscription, idx) in pastSubscriptions" :key="idx+100" class="kpt-subscription-record is-flex">
        <div>
          <detail-field>
            <template v-slot:label>Product</template>
            <template v-slot:value>{{nonActiveSubscription.productName}}</template>
          </detail-field>

          <detail-field v-if="nonActiveSubscription.hasSatellite">
            <template v-slot:label>Region</template>
            <template v-slot:value>{{nonActiveSubscription.regionName || '-'}}</template>
          </detail-field>
          <detail-field v-if="nonActiveSubscription.hasSatellite">
            <template v-slot:label>Data</template>
            <template v-slot:value>{{nonActiveSubscription.dataPackageName || '-'}}</template>
          </detail-field>  
          <detail-field>
            <template v-slot:label>Start Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(nonActiveSubscription.startDate), new Date(nonActiveSubscription.startDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>End Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(nonActiveSubscription.endDate), new Date(nonActiveSubscription.endDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Auto Renew</template>
            <template v-slot:value>{{ nonActiveSubscription.autoRenew ? 'Yes' : 'No' }}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>End Reason</template>
            <template v-slot:value>{{nonActiveSubscription.endReason || '-'}}</template>
          </detail-field>
          <div v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
            <detail-field>
              <template v-slot:label>Purchase Order</template>
              <template v-slot:value>{{ nonActiveSubscription.purchaseOrder || '-' }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Billed Account</template>
              <template v-slot:value>{{ getAccountName(nonActiveSubscription.billedAccountId) || '-' }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Is Billable</template>
              <template v-slot:value>{{ nonActiveSubscription.isBillable ? `Yes` : `No` }}</template>
            </detail-field>
          </div>
        </div>
        <div class="is-flex" style="justify-content: flex-end; flex: 1 0 auto;">
          <b-button tag="router-link" type="is-link is-outlined" size="is-small" :to="{ name: 'subscriptionOverview', params: { id: nonActiveSubscription.id }}" v-if="!isSharedRemote">View Subscription</b-button>
        </div>
      </div>
    </template>
    <div v-else>
      <span>No past subscriptions.</span>
    </div>
    <!-- <hr>
    <b-table :data="compositeTableData" striped>
      <template slot-scope="props">
        <b-table-column field="month" label="Date" sortable>
          {{format(props.row.month, 'yyyy-MM')}}
        </b-table-column>
        <b-table-column field="satelliteLimit" label="Data Limit">
          {{$numeral(props.row.satelliteLimit).format('0.00b')}}
        </b-table-column>
        <b-table-column field="satelliteUsed" label="Data Used" sortable>
          {{$numeral(props.row.satelliteUsed).format('0.00b')}}
        </b-table-column>
        <b-table-column field="satelliteOverage" label="Overage" sortable>
          {{$numeral(props.row.satelliteOverage).format('0.00b')}}
        </b-table-column>
      </template>
    </b-table> -->
  </div>
</template>
<script>
import { orderBy } from 'lodash';
// eslint-disable-next-line object-curly-newline
import { isBefore, isAfter, format, addMinutes } from 'date-fns';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import { dateUtilityMixin } from '../../shared/mixins/dateUtilityMixin';

export default {
  data() {
    return {
      format,
      addMinutes,
      seedTable: [],
      cancellingSubscriptions: false
    }
  },
  mixins: [idToNameParserMixin, dateUtilityMixin],
  computed: {
    remote() {
      return this.$store.state.remotes.remote;
    },
    activeSubscription() {
      return this.$store.getters.activeSubscription;
    },
    nonActiveSubscriptions() {
      return orderBy(this.$store.state.remotes.remote.subscriptions?.filter(s => !s.active), ['endDate'], ['desc']);
    },
    pastSubscriptions() {
      return this.nonActiveSubscriptions.filter(s => isBefore(this.getDateInUTCFormat(new Date(s.endDate)), this.getDateInUTCFormat(new Date())));
    },
    futureSubscriptions() {
      return this.nonActiveSubscriptions.filter(s => s.endReason != 'UserTerminated' && isAfter(this.getDateInUTCFormat(new Date(s.startDate)), this.getDateInUTCFormat(new Date())));
    },
    usageHistory() {
      return this.$store.getters.remoteHistoricalMetrics;
    },
    accounts() {
      return this.$store.state.accounts.accounts;
    },
    isSharedRemote() {
      // flatten array to just account id's
      const hierarchyAccountIds = this.accounts.map(a => a.id);
      // check to see if the remote account matches the users account hierarchy
      const hierarchyAccountFound = hierarchyAccountIds.includes(this.remote.account.id);
      return !hierarchyAccountFound;
    }
  },
  methods: {
    getEarliestSubscriptionDate(subscriptions) {
      let earliestSubscriptionDate = new Date();
      if (!subscriptions.length) return earliestSubscriptionDate;
      subscriptions.forEach(sub => {
        if (!sub.startDate) return;
        const startDate = new Date(sub.startDate);
        if (isBefore(startDate, earliestSubscriptionDate)) {
          earliestSubscriptionDate = startDate;
        }
      })
      return earliestSubscriptionDate;
    },
    getLastSubscriptionDate(subscriptions) {
      let latest = this.getEarliestSubscriptionDate(this.remote.subscriptions);
      if (!subscriptions.length) return latest;
      subscriptions.forEach(sub => {
        if (!sub.endDate) return;
        const endDate = new Date(sub.endDate);
        if (isAfter(endDate, latest)) {
          latest = endDate;
        }
      })
      if (isAfter(latest, new Date())) latest = new Date();
      return latest;
    },
    getAccountName(accountId) {
      if (!accountId || !this.accounts) return null;
      const account = this.accounts.find(a => a.id == accountId);
      return account ? account.name : null;
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/colors';

.kpt-subscription-record {
  margin-bottom: 8px;
  padding: 8px;
  background: $white-bis;

  &.kpt-subscription-cancel {
    height: 208px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}
</style>
